/**
 * Welcome offer (design A): the corner card and small tab (desktop), the slim bar (phones and
 * tablets) and the banner above the footer (every page).
 *
 * - No libraries. Loaded with `defer` by sections/welcome-offer.liquid and
 *   sections/welcome-offer-banner.liquid; a guard makes sure it runs only once per page.
 * - The card's settings arrive as data-* attributes on [data-welcome-offer].
 * - What the visitor did is kept in this browser only: localStorage "ic_welcome_offer"
 *   (shown, closed, tabClosed, subscribed) and, for this visit, sessionStorage "ic_wo_place", "ic_wo_ad"
 *   and "ic_wo_sent" (which form was just sent, kept for 30 minutes).
 * - Card or bar: the card only on a wide screen whose main pointer is a mouse or trackpad (CARD_SCREEN).
 *   Phones, tablets and other touch screens (iPads held sideways too) never get a pop-up: they get a bar
 *   that sits in the page and opens when tapped.
 * - After a sign-up the form is swapped for a short thank-you (class "is-done" on the card/bar root or on the
 *   banner). Its "Use a different email" button (data-wo-again) takes "is-done" off again and puts the cursor
 *   in the email box, so a mistyped address can be fixed without refreshing the page.
 * - Coming back from a sign-up, the page returns to where the visitor was (the footer banner at the same height on
 *   the screen) and keeps it there for up to 6 seconds while the page above it finishes loading (stayAt).
 */
(() => {
  'use strict';

  if (window.icWelcomeOffer) return;
  window.icWelcomeOffer = true;

  const STORAGE_KEY = 'ic_welcome_offer';
  const PLACE_KEY = 'ic_wo_place';
  const AD_KEY = 'ic_wo_ad';
  const SENT_KEY = 'ic_wo_sent';
  const SENT_FOR = 30 * 60 * 1000;
  const DAY = 24 * 60 * 60 * 1000;
  // Card or bar. Must match the bar's inline styles (sections/welcome-offer.liquid) and the card's styles
  // (assets/welcome-offer.css), so nothing flashes or shifts. Touchscreen laptops with a trackpad keep the card.
  const CARD_SCREEN = '(min-width: 990px) and (hover: hover) and (pointer: fine)';

  // Sign-up forms are posted the normal way (not with fetch): Shopify's own bot check only completes
  // a normal post. Shopify then brings the visitor back with ?customer_posted=true, and the end of the
  // address says which of our forms it was. The pre-paint scripts in both sections read these too.
  const THANKS_CARD = '#wo-thanks';
  const THANKS_BANNER = '#wo-thanks-banner';

  // Ad clicks carry tags like these. Visitors from an ad get the small tab, never the card by itself.
  const AD_TAGS = /[?&](gclid|gbraid|wbraid|gad_source|dclid|msclkid)=|[?&]utm_medium=(cpc|ppc|paid)/i;

  const MESSAGES = {
    consent: 'Please tick the box so we know it is OK to email you.',
    sending: 'Sending...',
    preview: 'Preview only: sign-ups are switched off inside the theme editor.',
  };

  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const toNumber = (value) => (Number.isFinite(parseFloat(value)) ? parseFloat(value) : 0);

  /* ---------- What this visitor did before (kept in this browser only) ---------- */

  let memory = {};
  const load = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || memory;
    } catch (error) {
      return memory;
    }
  };
  const remember = (patch) => {
    if (designMode) return;
    memory = Object.assign({}, load(), patch);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(memory));
    } catch (error) {
      /* storage is blocked: remembered for this page view only */
    }
  };

  const visit = {
    get(key) {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch (error) {
        /* storage is blocked */
      }
    },
    drop(key) {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        /* storage is blocked */
      }
    },
  };

  /* ---------- Coming back to the same spot after signing up ---------- */

  // Signing up reloads the page. Before it goes, note how far down the visitor was and, for the footer
  // banner, where the banner sat on the screen (the page above it can be a little different on return).
  const keepPlace = (anchor) => {
    visit.set(PLACE_KEY, JSON.stringify({
      y: Math.round(window.scrollY),
      a: anchor ? Math.round(anchor.getBoundingClientRect().top) : null,
    }));
  };

  const takePlace = () => {
    let place = null;
    try {
      place = JSON.parse(visit.get(PLACE_KEY));
    } catch (error) {
      place = null;
    }
    visit.drop(PLACE_KEY);
    return place && Number.isFinite(place.y) ? place : { y: 0, a: null };
  };

  // The banner back at the same height on the screen, or else the same distance down the page.
  const placeTop = (place, anchor) =>
    Math.max(0, anchor && Number.isFinite(place.a) ? anchor.getBoundingClientRect().top + window.scrollY - place.a : place.y);

  // Scroll position that puts an element in the middle of the screen (or at the top, if it's taller).
  const centreOn = (el) => {
    const r = el.getBoundingClientRect();
    return Math.max(0, r.top + window.scrollY - Math.max(0, (window.innerHeight - r.height) / 2));
  };

  const scrollNow = (top) => {
    const root = document.documentElement;
    const before = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto'; // jump straight there, even if the theme scrolls smoothly
    window.scrollTo(0, top);
    root.style.scrollBehavior = before;
  };

  // Go to the spot now, and keep it there while the page finishes loading: images and sections above it can
  // still change the page's height (for example the home page's "Recently viewed" row is added a moment after
  // the page has loaded, and would push the thank-you down and off the screen). So for a few seconds the spot is
  // put back whenever the page changes height, unless the visitor has started scrolling or tapping themselves.
  const STAY_FOR = 6000;
  const stayAt = (getTop) => {
    let moved = false;
    let watcher = null;
    const release = () => {
      moved = true;
      if (watcher) watcher.disconnect();
    };
    ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((type) => {
      window.addEventListener(type, release, { once: true, passive: true });
    });
    const settle = () => {
      const top = getTop();
      if (!moved && Math.abs(window.scrollY - top) > 2) scrollNow(top);
    };
    scrollNow(getTop());
    if (document.readyState === 'complete') settle();
    else window.addEventListener('load', settle, { once: true });
    if (typeof ResizeObserver === 'function') {
      watcher = new ResizeObserver(settle);
      // The page's height shows in the body: the store's theme gives the html element only the height of the window.
      watcher.observe(document.documentElement);
      watcher.observe(document.body);
      setTimeout(() => watcher.disconnect(), STAY_FOR);
    }
  };

  // When something closes under the keyboard, keep focus on the page instead of losing it.
  const ownsFocus = (el) => !document.activeElement || document.activeElement === document.body || el.contains(document.activeElement);
  const focusPage = () => {
    const main = document.getElementById('MainContent') || document.querySelector('main');
    if (!main) return;
    if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
    main.focus({ preventScroll: true });
  };

  // Microsoft Clarity is loaded late by the theme. If it is not there yet, skip quietly.
  const track = (name) => {
    try {
      if (typeof window.clarity === 'function') window.clarity('event', name);
    } catch (error) {
      /* analytics must never break the page */
    }
  };

  /* ---------- Back from a sign-up, or from an address Shopify turned down ---------- */

  const posted = /[?&]customer_posted=true/.test(window.location.search);
  const hash = window.location.hash;
  if (posted) remember({ subscribed: Date.now() });

  // The form this visitor just sent (noted when it was sent). Shopify's bot check can lose the end of the
  // address, so this tells us where to say thanks, or which form holds Shopify's error message.
  let sent = null;
  try {
    sent = JSON.parse(visit.get(SENT_KEY));
  } catch (error) {
    sent = null;
  }
  if (!sent || !(Date.now() - sent.t < SENT_FOR)) sent = null;

  // Where the thank-you goes: our marker if it's there; if the end of the address is missing, the form they
  // used, or else the footer banner. Another form's marker: that form says thanks itself.
  // (Same rule as the pre-paint scripts in both sections.)
  let back = null;
  if (posted) {
    if (hash === THANKS_CARD || hash === THANKS_BANNER) back = hash;
    else if (sent) back = sent.to;
    else if (!hash) back = THANKS_BANNER;
  }

  // Shopify turned the address down: it reloads the page with its message inside our forms.
  const errorShown = !posted && !designMode && Boolean(document.querySelector('.wo-form [data-wo-error]'));
  if (posted || errorShown) visit.drop(SENT_KEY);

  if (AD_TAGS.test(window.location.search)) visit.set(AD_KEY, '1');
  const fromAd = visit.get(AD_KEY) === '1';

  // Tidy the address bar, so a refresh or a bookmark is not a "thank you" page.
  const tidyAddress = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('customer_posted');
    const query = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : ''));
  };

  /* ---------- Sign-up forms (card, phone bar and footer banner) ---------- */

  const say = (form, text, quiet) => {
    const status = form.querySelector('[data-wo-status]');
    if (!status) return;
    status.textContent = text;
    status.hidden = !text;
    status.classList.toggle('is-info', Boolean(quiet));
  };

  const wireForms = (root, thanks, anchor) => {
    root.addEventListener('submit', (event) => {
      const form = event.target.closest('.wo-form');
      if (!form) return;
      if (designMode) {
        event.preventDefault();
        say(form, MESSAGES.preview);
        return;
      }

      // Nothing is cancelled here: Shopify's bot check finishes the post, then the page reloads.
      // The address ending points at nothing on the page, so the browser does not scroll anywhere on return.
      form.setAttribute('action', form.getAttribute('action').split('#')[0] + thanks);
      keepPlace(anchor);
      visit.set(SENT_KEY, JSON.stringify({ to: thanks, form: form.id, t: Date.now() }));
      const button = form.querySelector('[type="submit"]');
      button.disabled = true;
      say(form, MESSAGES.sending, true);
      // If the bot check is closed without finishing, the button works again.
      setTimeout(() => {
        button.disabled = false;
        say(form, '');
      }, 20000);
    });

    root.querySelectorAll('.wo-consent-box').forEach((box) => {
      box.addEventListener('invalid', () => box.setCustomValidity(MESSAGES.consent));
      box.addEventListener('change', () => box.setCustomValidity(''));
    });

    // "Use a different email" in the thank-you (shown right after a sign-up): the sign-up form comes back
    // in the same place, with the cursor in the email box. (Not a refresh: the thank-you is still on screen.)
    // Only this card, bar or banner changes; the footer's quiet line (if it is showing) stays as it is.
    root.addEventListener('click', (event) => {
      const again = event.target.closest('[data-wo-again]');
      if (!again) return;
      root.classList.remove('is-done');
      const body = again.closest('.wo-body');
      const field = body && body.querySelector('input[type="email"]');
      if (field) field.focus();
      track('welcome_offer_other_email');
    });
  };

  // Decided once, when the page opens: where the thank-you goes, and which form to bring into view
  // when Shopify's error message is showing.
  let thanksIn = null;
  let failed = null;
  const decide = () => {
    const card = document.querySelector('[data-welcome-offer]');
    const banner = document.querySelector('[data-wo-banner]');
    if (back === THANKS_CARD) thanksIn = card ? 'card' : banner ? 'banner' : null;
    else if (back === THANKS_BANNER) thanksIn = banner ? 'banner' : card ? 'card' : null;
    if (errorShown) {
      const used = sent && sent.form ? document.getElementById(sent.form) : null;
      const useCard = window.matchMedia(CARD_SCREEN).matches;
      failed = used || document.querySelector(useCard ? '.wo-card .wo-form' : '.wo-bar .wo-form') || document.querySelector('.wo-banner .wo-form');
    }
  };

  /* ---------- Card, tab and phone bar (header) ---------- */

  const initCard = (root, first) => {
    const html = document.documentElement;
    const card = root.querySelector('[data-wo-card]');
    const tab = root.querySelector('[data-wo-tab]');
    const opener = root.querySelector('[data-wo-open]');
    const useCard = window.matchMedia(CARD_SCREEN).matches;
    const config = {
      seconds: toNumber(root.dataset.seconds),
      scroll: toNumber(root.dataset.scroll),
      exitIntent: root.dataset.exitIntent === 'true',
      exitSeconds: toNumber(root.dataset.exitSeconds),
      quietDays: toNumber(root.dataset.quietDays),
      checkCart: root.dataset.checkCart === 'true',
    };
    const quietTimeOver = (time) => config.quietDays > 0 && Date.now() - time > config.quietDays * DAY;

    const thanksHere = first && !designMode && thanksIn === 'card';
    const fixHere = first && failed && root.contains(failed) ? failed : null;
    const state = designMode ? {} : load();
    if (state.subscribed && !thanksHere) {
      html.classList.add('wo-off');
      root.remove();
      return;
    }

    // The small tab: with the card only, and not once the visitor has closed it for good or signed up
    // (including a visitor who signed up and then chose "Use a different email" in the thank-you).
    const tabAllowed = () => useCard && !load().tabClosed && !load().subscribed && !root.classList.contains('is-done');

    const show = (auto) => {
      card.hidden = false;
      tab.hidden = true;
      if (auto) {
        remember({ shown: Date.now() });
      } else {
        (card.querySelector('input[type="email"]') || card).focus({ preventScroll: true });
      }
      track(auto ? 'welcome_offer_shown' : 'welcome_offer_opened');
    };

    const hide = () => {
      card.hidden = true;
      tab.hidden = !tabAllowed();
      remember({ closed: Date.now() });
      track('welcome_offer_closed');
    };

    // Closing the card (x, No thanks, Keep browsing or Escape): keyboard focus moves to the small tab,
    // or back to the page if there is no tab.
    const closeCard = () => {
      const hadFocus = ownsFocus(card);
      hide();
      if (!hadFocus) return;
      if (!tab.hidden) opener.focus({ preventScroll: true });
      else focusPage();
    };

    const hideTab = () => {
      const hadFocus = ownsFocus(tab);
      tab.hidden = true;
      remember({ tabClosed: Date.now() });
      track('welcome_offer_tab_closed');
      if (hadFocus) focusPage();
    };

    const hideBar = () => {
      const hadFocus = ownsFocus(root.querySelector('[data-wo-bar]'));
      html.classList.add('wo-off');
      remember({ closed: Date.now() });
      track('welcome_offer_bar_closed');
      if (hadFocus) focusPage();
    };

    root.addEventListener('click', (event) => {
      const target = event.target.closest('[data-wo-dismiss], [data-wo-open], [data-wo-tab-close]');
      if (!target) return;
      if (target.hasAttribute('data-wo-open')) show(false);
      else if (target.hasAttribute('data-wo-tab-close')) hideTab();
      else if (target.closest('[data-wo-bar]')) hideBar();
      else closeCard();
    });

    card.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeCard();
    });

    wireForms(root, THANKS_CARD, null);

    // The page this form sent the visitor back to: say thanks, right where they signed up.
    if (thanksHere) {
      html.classList.remove('wo-off');
      root.classList.add('is-done');
      tab.hidden = true;
      if (useCard) card.hidden = false;
      else root.querySelector('.wo-bar-details').open = true;
      const thanks = [...root.querySelectorAll('[data-wo-success]')].find((el) => el.offsetParent !== null);
      if (thanks) thanks.focus({ preventScroll: true });
      // Card: back to the spot they were reading. Bar: it is at the very top.
      const place = takePlace();
      stayAt(() => (useCard ? place.y : 0));
      tidyAddress();
      track('welcome_offer_signup');
      return;
    }

    // Shopify turned the address down: open the form they used, so its message can be read and fixed.
    if (fixHere) {
      html.classList.remove('wo-off');
      if (fixHere.closest('[data-wo-card]')) {
        show(false); // also puts the cursor in the email box
      } else {
        root.querySelector('.wo-bar-details').open = true;
        const field = fixHere.querySelector('input[type="email"]');
        if (field) field.focus({ preventScroll: true });
        stayAt(() => 0);
      }
      track('welcome_offer_error');
      return;
    }

    /* ---------- When the desktop card opens by itself ---------- */

    if (designMode) {
      card.hidden = false; // in the theme editor, always show it so the wording can be edited
      return;
    }

    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const seenRecently = Boolean(state.shown) && !quietTimeOver(state.shown);

    // Once the card has been seen or closed, or the visitor came from an ad, the small tab offers it instead.
    tab.hidden = !((state.shown || state.closed || fromAd) && tabAllowed());

    // Never by itself: where the bar is used (phones, tablets, touch screens), for visitors from an ad, or when
    // it has already been shown (unless the quiet period is over). It never opens on arrival either.
    if (!useCard || fromAd || seenRecently) return;
    if (!config.seconds && !config.scroll && !(config.exitIntent && canHover)) return;

    let finished = false;
    let waiting = false;
    let timer = 0;
    let startedAt = 0;
    let visibleMs = 0;
    let scrollQueued = false;

    const busy = () => {
      const field = document.activeElement;
      const typing = field && (/^(INPUT|TEXTAREA|SELECT)$/.test(field.tagName) || field.isContentEditable);
      return Boolean(typing || /drawer-open|overflow-hidden/.test(document.body.className) || document.querySelector('dialog[open]'));
    };

    const cartHasItems = () =>
      fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then((response) => response.json())
        .then((cart) => cart.item_count > 0)
        .catch(() => false);

    const stopWatching = () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibility);
      html.removeEventListener('mouseleave', onLeave);
    };

    // Called by every trigger. Waits while the visitor is typing or a drawer is open.
    const attempt = async () => {
      if (finished || waiting) return;
      waiting = true;
      for (let tries = 0; tries < 12 && busy(); tries += 1) await pause(5000);
      waiting = false;
      if (finished || busy()) return;
      finished = true;
      stopWatching();
      if (config.checkCart && (await cartHasItems())) return;
      if (load().subscribed) return;
      show(true);
    };

    // Time on the page counts only while the page is open and in front.
    const startClock = () => {
      const left = config.seconds * 1000 - visibleMs;
      if (document.visibilityState !== 'visible' || left <= 0) return;
      startedAt = Date.now();
      timer = setTimeout(() => {
        timer = 0;
        visibleMs = config.seconds * 1000;
        attempt();
      }, left);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        startClock();
      } else if (timer) {
        clearTimeout(timer);
        timer = 0;
        visibleMs += Date.now() - startedAt;
      }
    };

    const onScroll = () => {
      if (scrollQueued) return;
      scrollQueued = true;
      requestAnimationFrame(() => {
        scrollQueued = false;
        const seen = ((window.scrollY + window.innerHeight) / html.scrollHeight) * 100;
        if (seen >= config.scroll) attempt();
      });
    };

    // Product pages: only when the mouse heads for the top edge of the window, as if leaving.
    const onLeave = (event) => {
      if (event.clientY <= 0 && performance.now() >= config.exitSeconds * 1000) attempt();
    };

    if (config.seconds > 0) {
      startClock();
      document.addEventListener('visibilitychange', onVisibility);
    }
    if (config.scroll > 0) window.addEventListener('scroll', onScroll, { passive: true });
    if (config.exitIntent && canHover) html.addEventListener('mouseleave', onLeave);
  };

  /* ---------- Footer banner ---------- */

  const initBanner = (banner, first) => {
    wireForms(banner, THANKS_BANNER, banner);

    // Someone who has already signed up sees one quiet line here. "Use a different email" brings the sign-up
    // back in its place (for this page view), with the cursor in the email box.
    const another = banner.querySelector('[data-wo-another]');
    if (another) {
      another.addEventListener('click', () => {
        document.documentElement.classList.remove('wo-joined');
        const field = banner.querySelector('.wo-banner-form input[type="email"]');
        if (field) field.focus();
        track('welcome_offer_banner_other_email');
      });
    }

    if (designMode || !first) return;

    if (thanksIn === 'banner') {
      document.documentElement.classList.remove('wo-joined');
      banner.classList.add('is-done');
      const thanks = banner.querySelector('[data-wo-success]');
      if (thanks) thanks.focus({ preventScroll: true });
      // Signed up here: the banner back at the same height on the screen. Otherwise (the bot check lost
      // which form it was): bring the banner and its thank-you into view.
      const place = takePlace();
      stayAt(() => (Number.isFinite(place.a) ? placeTop(place, banner) : centreOn(banner)));
      tidyAddress();
      track('welcome_offer_banner_signup');
      return;
    }

    // Shopify turned the address down in this form: bring it into view with the cursor in the email box.
    // (If the form picked was in the card or bar and that was removed because this browser has already signed
    // up, the error is in this banner's form: show that one.)
    if (failed && (banner.contains(failed) || !failed.isConnected)) {
      document.documentElement.classList.remove('wo-joined'); // show the form, not the quiet line
      const here = banner.contains(failed) ? failed : banner.querySelector('.wo-form');
      const field = here && here.querySelector('input[type="email"]');
      if (field) field.focus({ preventScroll: true });
      stayAt(() => centreOn(banner));
      track('welcome_offer_error');
    }
  };

  const start = (scope, first) => {
    const root = scope.querySelector('[data-welcome-offer]');
    if (root) initCard(root, first);
    const banner = scope.querySelector('[data-wo-banner]');
    if (banner) initBanner(banner, first);
  };

  decide();
  start(document, true);

  // The theme editor redraws a section when a setting changes.
  document.addEventListener('shopify:section:load', (event) => start(event.target, false));
})();
